import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Collapse,
  Alert,
  Tooltip,
  Link as MuiLink,
} from '@mui/material';
import {
  CheckCircle,
  Error as ErrorIcon,
  Schedule,
  Edit,
  History,
  OpenInNew,
} from '@mui/icons-material';
import { alpha, useTheme, Theme } from '@mui/material/styles';
import { TradingAlert } from '../types/dashboard';
import { formatTriggerTime, formatExecutionTime } from '../utils/formatters';
import { formatRecentAlertMessage } from '../utils/alertUserFacingText';
import { formatWebhookDestination } from '../utils/webhookDestination';

interface AlertFeedProps {
  alerts: TradingAlert[];
  loading?: boolean;
}

type FeedTone = 'sent' | 'hold' | 'error';

function statusHint(alert: TradingAlert): string | null {
  const friendly = formatRecentAlertMessage(alert.errorMessage, alert.status);
  if (friendly) return friendly;
  if (alert.status === 'error' && !alert.errorMessage) {
    return 'Execution failed. Open Settings → Incoming webhook activity for the full request log.';
  }
  return null;
}

function feedStatus(alert: TradingAlert): { label: string; tone: FeedTone } {
  let outcome: string;
  let tone: FeedTone;
  switch (alert.status) {
    case 'success':
      outcome =
        alert.executionTime > 0
          ? `Sent · delivered ${formatExecutionTime(alert.executionTime)}`
          : 'Sent · delivered';
      tone = 'sent';
      break;
    case 'error':
      outcome = 'Delivery failed';
      tone = 'error';
      break;
    case 'low_confidence':
      outcome = 'Low confidence';
      tone = 'hold';
      break;
    case 'pending':
    default:
      outcome = 'No trade sent';
      tone = 'hold';
      break;
  }
  const side = alert.tradeSide?.trim();
  return { label: side ? `${side} · ${outcome}` : outcome, tone };
}

function toneColor(theme: Theme, tone: FeedTone): string {
  if (tone === 'sent') return theme.palette.success.main;
  if (tone === 'error') return theme.palette.error.main;
  return theme.palette.warning.main;
}

const AlertFeed: React.FC<AlertFeedProps> = ({ alerts, loading = false }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const getStatusIcon = (tone: FeedTone) => {
    if (tone === 'sent') return <CheckCircle sx={{ fontSize: 14 }} />;
    if (tone === 'error') return <ErrorIcon sx={{ fontSize: 14 }} />;
    return <Schedule sx={{ fontSize: 14 }} />;
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ fontFamily: theme.typography.h5.fontFamily, fontWeight: 500 }}>
          Recent activity
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Expand a row for details. Use <strong>Edit alert</strong> to fix webhooks, or{' '}
          <MuiLink
            component="button"
            variant="body2"
            onClick={() => navigate('/settings')}
            sx={{ verticalAlign: 'baseline' }}
          >
            Settings
          </MuiLink>{' '}
          for incoming trigger logs.
        </Typography>

        {loading && (
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        )}

        {!loading && alerts.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No execution log entries yet. Trigger an alert or incoming webhook to see activity here.
          </Alert>
        )}

        {alerts.map((alert, idx) => {
          const expanded = expandedId === alert.id;
          const hint = statusHint(alert);
          const status = feedStatus(alert);
          const color = toneColor(theme, status.tone);
          return (
            <Box
              key={alert.id}
              sx={{
                py: 1.5,
                borderTop: idx === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
                cursor: 'pointer',
              }}
              onClick={() => setExpandedId(expanded ? null : alert.id)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1, mb: 0.6 }}>
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color,
                    backgroundColor: alpha(color, 0.14),
                  }}
                >
                  {getStatusIcon(status.tone)}
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 600, flexGrow: 1, minWidth: 0 }} noWrap>
                  {alert.alertName?.trim() || alert.stockSymbol}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ flexShrink: 0, color: 'text.secondary', whiteSpace: 'nowrap' }}
                >
                  {formatTriggerTime(alert.timestamp)}
                </Typography>
              </Box>

              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  mb: 0.75,
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                  fontSize: 10,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  color,
                  backgroundColor: alpha(color, 0.12),
                }}
              >
                {status.label}
              </Box>

              {hint && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
                  {expanded || hint.length <= 96 ? hint : `${hint.slice(0, 96)}…`}
                </Typography>
              )}

              <Collapse in={expanded} onClick={(e) => e.stopPropagation()}>
                <Box sx={{ pt: 1.25 }}>
                  {(alert.stockSymbol || alert.positionType) && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {[alert.stockSymbol, alert.positionType].filter(Boolean).join(' · ')}
                    </Typography>
                  )}
                  {alert.webhookUrl && (
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Destination: {formatWebhookDestination(alert.webhookUrl)}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {alert.alertConfigId != null && alert.alertConfigId > 0 && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<Edit />}
                        onClick={() => navigate('/alerts', { state: { editAlertId: alert.alertConfigId } })}
                      >
                        Edit alert
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<History />}
                      onClick={() => navigate('/webhook-activity')}
                    >
                      Trigger activity log
                    </Button>
                    <Tooltip title="Open Alerts page">
                      <Button
                        size="small"
                        variant="text"
                        endIcon={<OpenInNew fontSize="small" />}
                        onClick={() => navigate('/alerts')}
                      >
                        All alerts
                      </Button>
                    </Tooltip>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default AlertFeed;
